/* eslint-disable prefer-const */
/* eslint-disable camelcase */
import drushim_lodash from 'lodash'
import axios from 'axios'
import delay from '@/lib/delay'

/**
 * @typedef {object} SSADATA
 * @property {string} ssa_originId
 * @property {string} ssa_sessionId
 * @property {string} ssa_ssaHash
 * @property {string|null} ssa_abtest
 * @property {string|null} ssa_userHashId
 * @property {string|null} ssa_mySteptSoneId
 * @property {string|null} ssa_cid
 * @property {*} ssa_what
 * @property {*} ssa_where
 * @property {*} ssa_listingids
 * @property {number} ssa_numberOfTotalResults
 * @property {number} ssa_pageNumber
 * @property {number} ssa_resultsPerPage
 * @property {*} ssa_offset
 * @property {string} ssa_sort
 * @property {string} ssa_searchType
 * @property {string} ssa_jobAgentId
 * @property {boolean} IsPreferred
 * @property {string[]} PreferredIds
 * @property {boolean} IsHot
 * @property {string[]} HotIds
 * @property {object} ssa_Filters
 * @property {*} ssa_jobid
 * @property {*} ssa_positionOnPage
 * @property {*} ssa_positionOnResultList
 * @property {*} IsRecommendadJobPreferred
 * @property {string[]} RecommendadJobsPreferredIds
 * @property {string} ABTestCookie
 * @property {string} CookieExperation
 *
 * @example
 * {
 * ssa_originId: "10000"
 * ssa_sessionId: "rxryblgw14xgfoabatvjoq4d"
 * ssa_ssaHash: "182608901"
 * ssa_abtest: null
 * ssa_cid: null
 * ssa_userHashId: "96ea290b-4ae2-4998-9b48-b05d856b12ff"
 * ssa_userId: null
 * }
 */

/**
 * The response from /ssadata endpoint
 * @typedef SSADATAResponse
 * @property {string} ssa_originId
 * @property {string} ssa_sessionId
 * @property {string} ssa_ssaHash
 * @property {string|null} ssa_abtest
 * @property {string|null} ssa_userHashId
 * @property {string|null} ssa_mySteptSoneId
 * @property {string|null} ssa_cid
 */

function newData(options = {}) {
    // for all events
    let ssa_originId
    let ssa_sessionId
    let ssa_ssaHash
    let ssa_abtest
    let ssa_userHashId
    let ssa_mySteptSoneId
    let ssa_cid = null

    // for search events
    let ssa_what
    let ssa_where
    let ssa_listingids
    let ssa_numberOfTotalResults
    let ssa_pageNumber = 1
    let ssa_resultsPerPage = 10
    let ssa_offset
    let ssa_sort = 'date+'
    let ssa_searchType = ''
    let ssa_jobAgentId = ''

    let IsPreferred
    let PreferredIds = []
    let IsHot
    let HotIds = []
    let ssa_Filters = {}

    // for ListingView
    let ssa_jobid
    let ssa_positionOnPage
    let ssa_positionOnResultList
    let IsRecommendadJobPreferred
    let RecommendadJobsPreferredIds = []

    /**
     * @type {SSADATA}
     */
    const ssaData = {
        ssa_originId,
        ssa_sessionId,
        ssa_ssaHash,
        ssa_abtest,
        ssa_userHashId,
        ssa_mySteptSoneId,
        ssa_cid,
        ssa_what,
        ssa_where,
        ssa_listingids,
        ssa_numberOfTotalResults,
        ssa_pageNumber,
        ssa_resultsPerPage,
        ssa_offset,
        ssa_sort,
        ssa_searchType,
        ssa_jobAgentId,
        ssa_Filters,
        ssa_jobid,
        ssa_positionOnPage,
        ssa_positionOnResultList,
        IsPreferred,
        PreferredIds,
        HotIds,
        IsHot,
        IsRecommendadJobPreferred,
        RecommendadJobsPreferredIds
    }

    options = drushim_lodash.cloneDeep(options);
    return drushim_lodash.assign(ssaData, options)
}

/**
 * SSA endpoints :
 * dev/alpha: https://alpha.eu-west-1.search.stepstone.com/ssa : 81.246.126.136
 * uat/beta: https://beta.eu-west-1.search.stepstone.com/ssa : 81.246.126.137
 * live:  https://live.eu-west-1.search.stepstone.com/ssa
 */

/**
 * Fetch ssa data from server
 * @async
 * @param {object} options
 * @param {boolean} option.retry - retry on fail
 * @returns {Promise<SSADATAResponse>} promise which resolves with ssa response data
 */
async function fetchSSADATA(options = {}) {
    // console.log(options)
    try {
        const requestConfig = options.requestConfig || {}
        const response = await axios.get(
            '/api/admin/ssa',
            // process.env.baseUrl + '/api/admin/ssa',

            requestConfig
        )
        return response.data
    } catch (e) {
        if (options.retry) {
            return retry(e)
        }
        throw e
    }

    /**
     * Retry fetching data from server
     * @throws when max retries are reached
     * @param {object} e error from fetch attempt
     * @returns {Promise} resolved with response data
     */
    function retry(e) {
        options.retryCount = options.retryCount || 0
        const maxRetry = options.maxRetry || Infinity
        if (options.retryCount >= maxRetry) {
            throw e
        }

        options.retryCount++
            const timeout = options.retryTimeout || 2000
        return delay(timeout).then(() => fetchSSADATA(options))
    }
}

export { newData, fetchSSADATA }
